import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { ChipSearchResult, PetProps } from '@barkinglabs/nano-shared';
import { Links } from '@/lib/constants';
import { weightString } from '@/lib/petUtils';
import { CommonChip } from '@/appview/lib/types';
import ContactModal, { searchResultToContactModalProps } from '../../modals/ContactModal/ContactModal';
import styles from './PetDetails.module.scss';
import PetDetailsEditableImage from './PetDetailsEditableImage';
import InfoCard from '../../InfoCard/InfoCard';
import type { ContactInfo } from '../ContactDetails/ContactDetails';
import { getContactInfo } from '../ContactDetails/ContactDetails';
import {
  type Pet,
  getPhotoUrl,
  getStatusText,
  getLastUpdatedText,
  getBreedText,
  getAgeText,
  getGenderText,
  getSpeciesText,
} from './PetDetails.helpers';
import { useSelector } from 'react-redux';
import { AppState } from '@/lib/store';
import { useQuery } from '@apollo/client';
import { userChipResellerQuery } from '@/lib/utils';
import { gqlTypes } from '@/types/gqlTypes';

interface PetDetailsProps {
  chip: Pick<CommonChip, 'serialNumber'>;
  pet: Pet | null;
  contactInfo?: ContactInfo;
  ownerNameOverride?: string;
  status?: 'pending' | 'active' | 'inactive';
  onPetEdited?(): void;
  searchResult?: ChipSearchResult | null;
  canEdit?: boolean;
  canEditPhoto?: boolean;
  canRecallAsReseller?: boolean;
  onRecallClicked?: () => void;
  onEditClicked?: () => void;
}

export default function PetDetails({
  chip,
  pet,
  contactInfo,
  ownerNameOverride,
  status,
  canEdit,
  canEditPhoto,
  canRecallAsReseller,
  onRecallClicked,
  searchResult,
  onEditClicked,
}: PetDetailsProps) {
  const session = useSelector((state: AppState) => state.session);
  const isLoggedIn = !!session;

  const { data: currentUserResellerData } = useQuery<gqlTypes.userChipReseller>(userChipResellerQuery, {
    skip: !isLoggedIn,
  });
  const isReseller = currentUserResellerData?.currentUser.chipReseller?.status === gqlTypes.ResellerStatus.ACCEPTED;

  if (!pet) {
    return <div>No details found for pet</div>;
  }
  const photoUrl = getPhotoUrl(pet);
  const statusText = getStatusText(status);
  const lastUpdatedText = getLastUpdatedText(pet);
  const breedText = getBreedText(pet);
  const ageText = getAgeText(pet);
  const genderText = getGenderText(pet);
  const speciesText = getSpeciesText(pet);
  const contactInfoAccessor = searchResult?.contactHistory ?? contactInfo;
  const { contactId, ownerName, email, phone, address, city } = getContactInfo(contactInfoAccessor);
  const displayOwnerName = ownerName || ownerNameOverride;
  const ownerRowValue = displayOwnerName ? `${displayOwnerName}${email ? ' (' + email + ')' : ''}` : 'No data';
  const serialNumber = searchResult ? searchResult.chip.serialNumber : chip.serialNumber;
  const canRequestTransfer = email !== session?.email && status !== 'pending' && !isReseller;

  const actionButtons = (
    <>
      {canRecallAsReseller && (
        <button className={styles.topRight} onClick={onRecallClicked}>
          Clear Owner
        </button>
      )}
      {canEdit && (
        <button className={styles.topRight} onClick={onEditClicked}>
          Edit
        </button>
      )}
      {!canEdit && !canRecallAsReseller && canRequestTransfer && (
        <button className={styles.topRight}>
          <Link to={Links.register({ id: serialNumber })}>Request Transfer</Link>
        </button>
      )}
    </>
  );

  const Header = (
    <>
      <div className={styles.header}>
        <div className={styles.picture}>
          <div>
            {(canEdit || canEditPhoto) && 'photos' in pet ? (
              <PetDetailsEditableImage pet={pet} />
            ) : (
              <img src={photoUrl} alt={pet.name} />
            )}
            <div
              className={classNames(
                styles.statusLabel,
                status === 'pending' && styles.pending,
                status === 'inactive' && styles.inactive,
              )}
            >
              {statusText}
            </div>
          </div>
        </div>
        <div className={styles.petName}>{pet.name}</div>
        <div className={styles.lastUpdated}>{lastUpdatedText}</div>
      </div>
      <div className={styles.chipId}>
        <div className={styles.chipIdLabel}>Chip ID</div>
        <div>{chip.serialNumber}</div>
      </div>
    </>
  );

  return (
    <div className={styles.main}>
      <InfoCard
        header={Header}
        rows={[
          [
            { label: 'Species', value: speciesText },
            { label: 'Breed', value: breedText },
          ],
          [
            { label: 'Gender', value: genderText },
            { label: 'Color', value: pet.color ?? 'No data' },
          ],
          [
            { label: 'Age', value: ageText },
            { label: 'Weight', value: weightString(pet) },
          ],
          [{ label: 'More info', value: pet.additionalInfo ?? 'No data' }],
          [{ label: 'Owner', value: ownerRowValue }],
          [{ label: 'Phone', value: phone ?? 'No data' }],
          address ? [{ label: 'Address', value: address }] : [{ label: 'City', value: city }],
        ]}
      />
      <div className={styles.buttons}>{actionButtons}</div>
      {searchResult && (ownerName ?? email) && (
        <ContactModal
          trigger={<button className="btn-black">Send Message</button>}
          {...searchResultToContactModalProps(searchResult)}
          contactTarget={{
            kind: 'contactHistory',
            id: contactId,
          }}
        />
      )}
    </div>
  );
}
